import { Inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import {
    COMPANY_VENDOR_ID,
    FLOORI_TRACKING_PROVIDER,
    flooriAssets,
    flooriAssetsNames,
    flooriFonts,
    flooriThemes,
} from '@floori-web/constants';
import { FlooriFontFamily, FlooriThemes, CompanyConfigDetailsDto } from '@floori/models';
import {
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    tap,
    withLatestFrom,
    from,
    filter,
    take,
    catchError,
} from 'rxjs';
import { AppFacade } from '@floori-web/store';
import {
    ConfigProvider,
    FLOORI_CONFIG_PROVIDER,
    FlooriEnv,
    LocalStorageKeys,
    StorageProvider,
} from '@floori-web/models';
import { BaseEffects } from '@floori-web/utils/abstract';
import { FlooriTrackingProvider } from '@floori-web/tracking';
import {
    FLOORI_ENV,
    FLOORI_STORAGE_PROVIDER,
    FLOORI_TRANSLATOR,
    FlooriIcons,
    flooriSpritePath,
    HashMap,
    Languages,
    TranslationsProvider,
    HtmlRendererService,
} from '@floori-web-components';
import * as ConfigActions from './config.actions';
import { SetupService } from '@floori-web/config';
import { NavigationEnd } from '@angular/router';

@Injectable()
export class ConfigEffects extends BaseEffects {
    private readonly initConfig$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ConfigActions.initConfig),
                withLatestFrom(this.configProvider.companyConfig$),
                tap(([_, config]) => this.appFacade.setSplashScreen(true, config?.logoSplash)),
                tap(() => {
                    if (this.setupService.endpoints?.['company']?.['configDetails']) {
                        this.store.dispatch(ConfigActions.loadConfigDetails());
                    }
                }),
                switchMap(([, config]) => {
                    if (!Object.keys(config || {}).length) {
                        return of([]);
                    }

                    if (config?.company?.integration?.type) {
                        this.appFacade.setIntegrationType(config?.company?.integration.type);
                    }

                    if (this.flooriEnv.darkThemeSupportIds?.split(',').includes(this.vendorId)) {
                        this.htmlRenderService.addClass(this.htmlRenderService.body, [
                            'floori-dark-theme',
                        ]);
                    }

                    if (
                        this.flooriEnv.clarityScriptSupportIds?.split(',').includes(this.vendorId)
                    ) {
                        this.htmlRenderService.addScript(
                            flooriAssets[flooriAssetsNames.clarityScript],
                        );
                    }

                    return this.renderClientPersonalisation(
                        config?.theme?.iconFamily || FlooriIcons.empty,
                        config?.theme?.fontFamily || FlooriFontFamily.sans,
                        config?.theme?.theme || FlooriThemes.default,
                        config?.themeCssVars || {},
                    ).pipe(map(() => config));
                }),
                tap(() => {
                    this.appFacade.checkStorageAvailability();
                    this.appFacade.checkCookies();
                }),
            ),
        { dispatch: false },
    );

    private readonly hideSplashOnNavigationEnd$ = createEffect(
        () =>
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                take(1),
                tap(() => this.appFacade.setSplashScreen(false)),
            ),
        { dispatch: false },
    );

    private readonly loadConfigDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ConfigActions.loadConfigDetails),
            switchMap(() =>
                from(this.setupService.fetchCompanyInfoDetails(this.setupService.clientKey)).pipe(
                    map((configDetails: CompanyConfigDetailsDto) =>
                        ConfigActions.loadConfigDetailsSuccess({ configDetails }),
                    ),
                    catchError(error => of(ConfigActions.loadConfigDetailsFailure({ error }))),
                ),
            ),
        ),
    );

    private readonly logFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ConfigActions.loadConfigDetailsFailure),
                tap(({ error }) =>
                    console.error('[FLOORI-SETUP] Config details failed to load.', error),
                ),
            ),
        { dispatch: false },
    );

    private readonly changeLang$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ConfigActions.changeLang),
                withLatestFrom(this.configProvider.companyConfig$),
                switchMap(([, config]) => {
                    const lang = config?.currentLang || Languages.en;
                    this.storage.set(LocalStorageKeys.visitorLang, lang).subscribe();
                    return this.translationProvider.changeLanguage(lang);
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private readonly appFacade: AppFacade,
        private readonly htmlRenderService: HtmlRendererService,
        private readonly setupService: SetupService,
        @Inject(FLOORI_ENV) private readonly flooriEnv: FlooriEnv,
        @Inject(COMPANY_VENDOR_ID) private readonly vendorId: string,
        @Inject(FLOORI_CONFIG_PROVIDER) private readonly configProvider: ConfigProvider,
        @Inject(FLOORI_STORAGE_PROVIDER) private readonly storage: StorageProvider,
        @Inject(FLOORI_TRANSLATOR) private readonly translationProvider: TranslationsProvider,
        @Inject(FLOORI_TRACKING_PROVIDER) private readonly trackingProvider: FlooriTrackingProvider,
    ) {
        super();
    }

    private renderClientPersonalisation(
        iconFamily: FlooriIcons,
        fontFamily: FlooriFontFamily,
        themeFamily: FlooriThemes,
        themeSettings?: HashMap<string>,
    ): Observable<void[]> {
        const fontFamilyName = fontFamily?.toLowerCase()?.trim();
        const font = flooriFonts[fontFamilyName as FlooriFontFamily];
        let theme = flooriThemes[themeFamily];
        theme = theme || flooriThemes[FlooriThemes.default];
        return forkJoin([
            this.htmlRenderService.renderLinkTag(
                'prefetch',
                `${flooriSpritePath}/${iconFamily}.svg`,
                'image/svg+xml',
                'image',
            ),
            this.htmlRenderService.renderLinkTag('stylesheet', font?.url || '', 'text/css').pipe(
                map(() => {
                    if (font?.family) {
                        this.htmlRenderService.addClass(this.htmlRenderService.body, [
                            `floori-${font.family}`,
                        ]);
                    }
                }),
            ),
            this.htmlRenderService
                .renderLinkTag('stylesheet', `${theme}.theme.css`, 'text/css')
                .pipe(
                    map(() => {
                        this.htmlRenderService.addClass(this.htmlRenderService.body, [theme]);
                    }),
                ),
        ]).pipe(
            map(() => {
                if (themeSettings) {
                    this.htmlRenderService.addCssVars(themeSettings);
                }

                return [];
            }),
        );
    }
}
